import {
  Check,
  Disc3,
  Heart,
  ListChecks,
  ListMusic,
  MoreHorizontal,
  Music2,
  Search,
  Shuffle,
  X,
} from "lucide-react";

import type { LibraryFilters } from "@/api/hooks";
import {
  CategoryTabs,
  type CategoryOption,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  IconButton,
  Input,
} from "@/components/ui";

export const LIBRARY_TABS = ["songs", "albums", "favorites", "playlists"] as const;
export type LibraryTab = (typeof LIBRARY_TABS)[number];

/** Reuses the hook's own union so the two can never drift apart. */
export const LIBRARY_SORTS = ["recent", "played", "title", "artist"] as const;
export type LibrarySort = NonNullable<LibraryFilters["sort"]>;

export const LIBRARY_PANEL_ID = "library-panel";

export function libraryTabId(tab: LibraryTab): string {
  return `library-tab-${tab}`;
}

const TABS: CategoryOption<LibraryTab>[] = [
  { value: "songs", label: "Songs", icon: Music2 },
  { value: "albums", label: "Albums", icon: Disc3 },
  { value: "favorites", label: "Favourites", icon: Heart },
  { value: "playlists", label: "Playlists", icon: ListMusic },
];

const FILTER_PLACEHOLDER: Record<LibraryTab, string> = {
  songs: "Filter by title, artist or album",
  albums: "Filter albums by title or artist",
  playlists: "Filter playlists",
  favorites: "Filter by title, artist or album",
};

const SORTS: { value: LibrarySort; label: string }[] = [
  { value: "recent", label: "Recently added" },
  { value: "played", label: "Recently played" },
  { value: "title", label: "Title" },
  { value: "artist", label: "Artist" },
];

export interface LibraryFilterFieldProps {
  tab: LibraryTab;
  query: string;
  onQueryChange: (query: string) => void;
}

/**
 * The filter box.
 *
 * Deliberately not part of the sticky toolbar. Pinning it cost a permanent 56px
 * on a phone to serve an occasional action, so it lives in the scrolling header
 * instead — visible when you are at the top of the list, which is where you are
 * when you decide to search it, and out of the way once you start scrolling.
 * The same place iOS keeps a list's search field.
 */
export function LibraryFilterField({
  tab,
  query,
  onQueryChange,
}: LibraryFilterFieldProps) {
  return (
    <Input
      type="search"
      value={query}
      onChange={(event) => onQueryChange(event.target.value)}
      placeholder={FILTER_PLACEHOLDER[tab]}
      aria-label="Filter library"
      autoComplete="off"
      // No form to submit, so the keyboard's Search key would otherwise do
      // nothing at all and stay up over the results.
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
      }}
      leading={<Search className="h-4 w-4" aria-hidden="true" />}
      trailing={
        query ? (
          <IconButton
            aria-label="Clear filter"
            size="sm"
            onClick={() => onQueryChange("")}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        ) : undefined
      }
      className="[&::-webkit-search-cancel-button]:appearance-none"
    />
  );
}

export interface LibraryToolbarProps {
  tab: LibraryTab;
  onTabChange: (tab: LibraryTab) => void;
  sort: LibrarySort;
  onSortChange: (sort: LibrarySort) => void;
  /** Shuffles and plays. Omitted when there is nothing playable to shuffle. */
  onShuffle?: () => void;
  shuffling?: boolean;
  shuffleDisabled?: boolean;
  /** Enters multi-select. Omitted for views that cannot be selected. */
  onStartSelection?: () => void;
  /** Selection is already on, so the button would be a no-op. */
  selectionActive?: boolean;
}

export function LibraryToolbar({
  tab,
  onTabChange,
}: Pick<LibraryToolbarProps, "tab" | "onTabChange">) {
  return (
    <CategoryTabs
      options={TABS}
      value={tab}
      onChange={onTabChange}
      label="Library view"
      tabId={libraryTabId}
      panelId={LIBRARY_PANEL_ID}
    />
  );
}

export function LibraryActions({
  tab, sort, onSortChange, onShuffle, shuffling, shuffleDisabled,
  onStartSelection, selectionActive = false,
}: Omit<LibraryToolbarProps, "onTabChange">) {
  const sortLabel = SORTS.find((option) => option.value === sort)?.label ?? SORTS[0].label;
  if (tab === "playlists") return null;
  return (
    <div className="flex shrink-0 items-center gap-2">
      {/* Albums are grouped by the server and cannot be multi-selected as a
            grid, so neither sorting nor selecting applies there. */}
      {tab === "albums" ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Sorting and selecting share one trigger. Both are occasional,
                  and two permanently visible buttons were what pushed this row
                  past the width of a phone. The current sort stays in the
                  accessible name so it is still announced without a label. */}
            <Button
              variant="secondary"
              aria-label={`More options. Sorted by ${sortLabel}`}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{sortLabel}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            {SORTS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onSelect={() => onSortChange(option.value)}
                trailing={
                  option.value === sort ? (
                    <Check
                      className="h-4 w-4 text-accent"
                      aria-hidden="true"
                    />
                  ) : null
                }
              >
                {option.label}
              </DropdownMenuItem>
            ))}

            {onStartSelection && !selectionActive ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  icon={ListChecks}
                  onSelect={onStartSelection}
                >
                  Select songs
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {/* Primary, and present on every tab including Albums: "just start
            listening" is the whole point and should not depend on which view
            you happen to be in. */}
      {onShuffle && !selectionActive ? (
        <Button
          aria-label="Shuffle and play your library"
          onClick={onShuffle}
          loading={shuffling}
          disabled={shuffleDisabled}
        >
          <Shuffle className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Shuffle</span>
        </Button>
      ) : null}

    </div>
  );
}
